import { Atividade, Dependencia } from '../models/atividade';

export interface NoCadeia {
  atividade: Atividade;
  dependencia?: Dependencia;
}

export interface CadeiaDependencia {
  id: string;
  caminhoCritico: boolean;
  nos: NoCadeia[];
}

/**
 * Agrupa dependências em cadeias lineares (origem -> destino -> destino...) para
 * renderização vertical simples — não é um layout de grafo genérico de propósito,
 * a maioria das cadeias reais do fechamento tem 2-4 elos.
 */
export const buildChains = (atividades: Atividade[], dependencias: Dependencia[]): CadeiaDependencia[] => {
  const porId = new Map(atividades.map(a => [a.id, a]));
  const idsComoDestino = new Set(dependencias.map(d => d.atividadeDestinoId));
  const raizes = dependencias.filter(d => !idsComoDestino.has(d.atividadeOrigemId));

  // Uma atividade pode desbloquear mais de uma outra (fan-out) — por isso a travessia usa
  // `filter` (todas as próximas dependências) em vez de `find` (só a primeira encontrada),
  // gerando 1 cadeia por caminho raiz→folha em vez de perder ramificações silenciosamente.
  const caminhos: NoCadeia[][] = [];

  const expandir = (dep: Dependencia, acumulado: NoCadeia[]): void => {
    const origem = porId.get(dep.atividadeOrigemId);
    const destino = porId.get(dep.atividadeDestinoId);
    if (!origem || !destino) return;

    const nosAteAqui = acumulado.length > 0 ? acumulado : [{ atividade: origem }];
    const proximosNos = [...nosAteAqui, { atividade: destino, dependencia: dep }];

    const proximasDependencias = dependencias.filter(d => d.atividadeOrigemId === dep.atividadeDestinoId);
    if (proximasDependencias.length === 0) {
      caminhos.push(proximosNos);
    } else {
      proximasDependencias.forEach(proxima => expandir(proxima, proximosNos));
    }
  };

  raizes.forEach(dep => expandir(dep, []));

  return caminhos.map((nos, index) => {
    const elos = nos.filter(n => n.dependencia);
    return {
      id: `cadeia-${index}`,
      // Critério é só pelos ELOS (dependências) desta cadeia específica — nunca pela flag
      // isolada de um nó, porque um nó pode ser compartilhado por outra cadeia não crítica
      // que se ramifica a partir dele (ex.: uma atividade do caminho crítico que também
      // desbloqueia, em paralelo, uma entrega secundária não crítica).
      caminhoCritico: elos.length > 0 && elos.every(n => n.dependencia?.caminhoCritico),
      nos,
    };
  });
};

export const filtrarCaminhoCritico = (cadeias: CadeiaDependencia[]): CadeiaDependencia[] =>
  cadeias.filter(c => c.caminhoCritico);
